import { ASSET_STORAGE_URL } from '~/modules/config';

// Slack hosts its media on two domains: avatars and CDN assets live on slack-edge.com
// (and subdomains such as avatars.slack-edge.com), while uploaded files live in Slack's
// S3 bucket, currently slack-files2.s3-us-west-2.amazonaws.com. mediaIds pointing at
// either are already fully qualified URLs and must not be prefixed with ASSET_STORAGE_URL.
const SLACK_MEDIA_HOST_SUFFIX = '.slack-edge.com';

// Matches Slack's S3 file bucket across region/endpoint variants: the bucket name
// (slack-files<n>), the S3 service with an optional region in either dot or dash form
// (s3, s3.eu-west-1, s3-us-west-2, ...), then amazonaws.com. Anchored (^...$) and run
// against the *parsed* hostname so a region change doesn't silently break media, while
// still rejecting partner buckets (insidesales.s3.amazonaws.com), lookalike suffixes
// (slack-files2...amazonaws.com.attacker.com), and prefix-glued hosts.
const SLACK_FILES_HOST_PATTERN = /^slack-files\d+\.s3([.-][a-z0-9-]+)?\.amazonaws\.com$/;
export const MEDIA_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
export const IMAGE_MEDIA_ID_PATTERN = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/;
const LEGACY_FILE_DOWNLOAD_PATH = '/servlet/servlet.FileDownload';
const LEGACY_FILE_DOWNLOAD_HOSTS = [
    'appexchange.salesforce.com',
    'partners.salesforce.com',
    'appxcommunities.force.com',
];
// QA/staging listings still serve carousel/logo media from Salesforce env hosts
// (e.g. appexchange.oaeappxqa.com, appexchange.oaestaging1.com), not only prod.
const APPEXCHANGE_ENV_HOST_SUFFIX_PATTERN = /(\.oaeappxqa\.com|\.oaestaging\d+\.com)$/;

function isAssetStorageMedia(mediaId: string): boolean {
    try {
        const mediaUrl = new URL(mediaId);
        const assetStorageUrl = new URL(ASSET_STORAGE_URL);
        const assetStoragePath = assetStorageUrl.pathname.replace(/\/$/, '');
        return (
            mediaUrl.protocol === 'https:' &&
            mediaUrl.origin === assetStorageUrl.origin &&
            (mediaUrl.pathname === assetStoragePath || mediaUrl.pathname.startsWith(`${assetStoragePath}/`))
        );
    } catch {
        return false;
    }
}

function isAllowedLegacyFileDownloadHost(hostname: string): boolean {
    return LEGACY_FILE_DOWNLOAD_HOSTS.includes(hostname) || APPEXCHANGE_ENV_HOST_SUFFIX_PATTERN.test(hostname);
}

function isLegacyFileDownloadMedia(mediaId: string): boolean {
    try {
        const url = new URL(mediaId);
        return (
            url.protocol === 'https:' &&
            isAllowedLegacyFileDownloadHost(url.hostname) &&
            url.pathname.endsWith(LEGACY_FILE_DOWNLOAD_PATH) &&
            url.searchParams.has('file')
        );
    } catch {
        return false;
    }
}

/**
 * Returns true only when mediaId is an absolute https URL hosted by Slack: either
 * slack-edge.com (apex or any subdomain such as avatars.slack-edge.com) or Slack's
 * S3 file bucket. Host-parsing rather than substring matching prevents lookalike hosts
 * (e.g. slack-edge.com.attacker.com) and path/query embedded matches from bypassing CDN
 * prefixing, since mediaId is partner-controlled data.
 */
function isSlackHostedMedia(mediaId: string): boolean {
    try {
        const { protocol, hostname } = new URL(mediaId);
        if (protocol !== 'https:') {
            return false;
        }
        return (
            hostname === 'slack-edge.com' ||
            hostname.endsWith(SLACK_MEDIA_HOST_SUFFIX) ||
            SLACK_FILES_HOST_PATTERN.test(hostname)
        );
    } catch {
        // Not an absolute URL — fall through to prefixing.
        return false;
    }
}

/**
 * Takes a complete, or partial asset URL and generates a the final URL of the asset
 * by appending ASSET_STORAGE_URL to incomplete urls, aka OAF mediaIds (which are uuids).
 * If the URL is a complete URL (aka legacy URLs), this function simply returns them.
 *
 * Shared by V1 and V2 media rendering (carousel, listing helpers, publisher logos,
 * server listing_service, etc.). Absolute https asset-storage / legacy FileDownload /
 * Slack hosts pass through unchanged. Non-URL strings that only *contain* those
 * prefixes (open-redirect class under the old includes() gates) are intentionally
 * prefixed with ASSET_STORAGE_URL — see V1 regression tests in asset_utils.test.ts.
 *
 * @param mediaId A legacy URL, or UUID that will be appended to ASSET_STORAGE_URL to produce the final URL.
 * @returns URL of media asset.
 */
export function generateMediaUrl(mediaId: string) {
    if (!mediaId) {
        return '';
    }

    // Slack-hosted media (avatars on slack-edge.com, uploaded files on S3) is already
    // a fully qualified URL — return as-is.
    if (isSlackHostedMedia(mediaId)) {
        return mediaId;
    }

    if (isAssetStorageMedia(mediaId)) {
        return mediaId;
    }

    if (isLegacyFileDownloadMedia(mediaId)) {
        return mediaId;
    }

    return `${ASSET_STORAGE_URL}/${mediaId}`;
}

export function isSupportedMediaUrl(mediaId: string): boolean {
    if (!mediaId) {
        return false;
    }

    if (isSlackHostedMedia(mediaId) || isAssetStorageMedia(mediaId) || isLegacyFileDownloadMedia(mediaId)) {
        return true;
    }

    return IMAGE_MEDIA_ID_PATTERN.test(mediaId);
}
